import { jsx, jsxs } from "react/jsx-runtime";
import { Box, FieldSet, AdornedInput, FormHelp, Legend, Popover, Paragraph, Heading } from "@zillow/constellation-10";
import React, { useRef, useEffect } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import useValidFieldUpdate from "../../utils/form/useValidFieldUpdate.esm.js";
import { fetchRate } from "../../utils/mortgagehelpers/mortgage-helpers.esm.js";
import { commaSeparateNumber, getCommaSeparatedNumber } from "../../utils/zhl-calculator.esm.js";
import { sendFieldInteractionEvent } from "./VALoanCalculator.Clickstream.esm.js";
import { FieldConfig, isSameFloat } from "./VALoanCalculator.utils.esm.js";
import { joinedSplitShell, joinedSplitMoney, joinedSplitAdornmentResetInlinePad, joinedSplitStartSymbol, joinedSplitRight, joinedSplitEndSymbol } from "./joined-split-input.styles.esm.js";
function DownPaymentField({
  isFHAEnabled = false,
  isMortgageComponent = false
}) {
  var _a, _b;
  const form = useFormContext();
  const proxyFormState = useFormState();
  const error = ((_a = proxyFormState == null ? void 0 : proxyFormState.errors[FieldConfig.downPayment.name]) == null ? void 0 : _a.message) || ((_b = proxyFormState == null ? void 0 : proxyFormState.errors[FieldConfig.downPaymentPercent.name]) == null ? void 0 : _b.message);
  const fieldAmount = form.register(FieldConfig.downPayment.name, FieldConfig.downPayment.config);
  const fieldPercent = form.register(FieldConfig.downPaymentPercent.name, FieldConfig.downPaymentPercent.config);
  const amountInputRef = useRef(null);
  const formatAmount = commaSeparateNumber;
  useEffect(() => {
    if (formatAmount) {
      const defaultAmountValue = form.getValues(fieldAmount.name);
      if (defaultAmountValue && amountInputRef.current) {
        amountInputRef.current.value = formatAmount(defaultAmountValue);
      }
    }
  }, []);
  useSyncDownPaymentPercent(isFHAEnabled, isMortgageComponent);
  return /* @__PURE__ */ jsx(Box, { css: {
    marginBottom: "tighter"
  }, children: /* @__PURE__ */ jsx(FieldSet, { css: {
    // Native <fieldset> padding/border shifts split inputs vs FormField rows (e.g. Home price).
    padding: 0,
    margin: 0,
    borderWidth: 0,
    borderStyle: "solid",
    borderColor: "transparent",
    minWidth: 0,
    width: "100%",
    outline: "none"
    // Note: this field renders ~4px taller than adjacent fields (Home price, ZIP code,
    // Interest rate). Those fields use v8 constellation AdornedInput (9px vert padding);
    // this field uses v10 constellation-10 AdornedInput (12px vert padding).
    // The difference will resolve once all fields are migrated to v10.
  }, legend: /* @__PURE__ */ jsx(Legend, { children: /* @__PURE__ */ jsxs(Box, { css: {
    display: "inline-flex",
    alignItems: "center"
  }, children: [
    "Down payment",
    isFHAEnabled && /* @__PURE__ */ jsx(Popover, { header: /* @__PURE__ */ jsx(Heading, { level: 6, children: "Down payment" }), body: /* @__PURE__ */ jsx(Paragraph, { children: "The minimum down payment for FHA loans is typically 3.5% with a credit score of 580 and higher and 10% for credit scores between 500 to 579." }) })
  ] }) }), description: (
    // Use blank space when no error or description to avoid
    // layout shift when when error is shown.
    /* @__PURE__ */ jsx(FormHelp, { error: Boolean(error), children: error || /* @__PURE__ */ jsx(React.Fragment, { children: " " }) })
  ), error: Boolean(error), required: true, children: /* @__PURE__ */ jsxs(Box, { className: joinedSplitShell(Boolean(error)), children: [
    /* @__PURE__ */ jsx(AdornedInput, { className: joinedSplitMoney, error: false, startAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { className: joinedSplitAdornmentResetInlinePad, children: /* @__PURE__ */ jsx("span", { className: joinedSplitStartSymbol, children: "$" }) }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { id: "downPaymentAmount", "aria-label": "Down payment", ...fieldAmount, ref: (el) => {
      amountInputRef.current = el;
      fieldAmount.ref(el);
    }, onInput: formatAmount ? (e) => {
      const target = e.target;
      target.value = formatAmount(target.value);
    } : void 0, autoComplete: "off" }) }),
    /* @__PURE__ */ jsx(AdornedInput, { className: joinedSplitRight(Boolean(error)), error: false, endAdornment: /* @__PURE__ */ jsx(AdornedInput.Adornment, { className: joinedSplitAdornmentResetInlinePad, children: /* @__PURE__ */ jsx("span", { className: joinedSplitEndSymbol, children: "%" }) }), input: /* @__PURE__ */ jsx(AdornedInput.Input, { "aria-label": "Down payment percent", ...fieldPercent, autoComplete: "off" }) })
  ] }) }) });
}
function useSyncDownPaymentPercent(isFHAEnabled, isMortgageComponent) {
  const form = useFormContext();
  const lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  useValidFieldUpdate(form, async (name, value) => {
    lastValidFields.current = {
      ...lastValidFields.current,
      [name]: value
    };
    if (name === FieldConfig.downPayment.name) {
      const nextDownPayment = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
      if (!isSameFloat(nextDownPayment, lastValidFields.current.downPaymentPercent)) {
        form.setValue(FieldConfig.downPaymentPercent.name, nextDownPayment, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      if (!isFHAEnabled) {
        sendFieldInteractionEvent("Down payment amount", "form_field", null);
      }
    }
    if (name === FieldConfig.downPaymentPercent.name) {
      const nextDownPayment = lastValidFields.current[FieldConfig.downPaymentPercent.name] / 100 * lastValidFields.current[FieldConfig.homePrice.name];
      if (!isSameFloat(nextDownPayment, lastValidFields.current.downPayment)) {
        form.setValue(FieldConfig.downPayment.name, getCommaSeparatedNumber(nextDownPayment), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
      if (!isFHAEnabled) {
        sendFieldInteractionEvent("Down payment percent", "form_field", null);
      }
    }
    if (name === FieldConfig.homePrice.name) {
      const nextDownPayment = lastValidFields.current[FieldConfig.downPayment.name] / lastValidFields.current[FieldConfig.homePrice.name] * 100;
      if (!isSameFloat(nextDownPayment, lastValidFields.current.downPaymentPercent)) {
        form.setValue(FieldConfig.downPaymentPercent.name, nextDownPayment, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
    if (name === FieldConfig.downPayment.name || name === FieldConfig.homePrice.name || name === FieldConfig.downPaymentPercent.name || name === (isFHAEnabled ? FieldConfig.fhaLoanProgram.name : FieldConfig.loanProgram.name)) {
      const newRate = await fetchRate({
        homePrice: lastValidFields.current[FieldConfig.homePrice.name],
        downPayment: Math.round(lastValidFields.current[FieldConfig.downPayment.name]),
        va: isFHAEnabled ? false : isMortgageComponent ? false : true,
        fha: isFHAEnabled ? true : false,
        loanProgram: lastValidFields.current[FieldConfig.loanProgram.name]
      });
      if (!isSameFloat(newRate, lastValidFields.current.interestRate)) {
        form.setValue(FieldConfig.interestRate.name, newRate, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }
    }
  });
}
export {
  DownPaymentField as default
};
